import "@/App.css";
import { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { LoadingScreen } from './components/LoadingScreen';
import { Navbar } from './components/Navbar';
import { TopBanner } from './components/TopBanner';
import { Hero } from './components/Hero';
import { ValueProposition } from './components/ValueProposition';
import { VideoSection } from './components/VideoSection';
import { FeaturedProperties } from './components/FeaturedProperties';
import { Casas } from './components/Casas';
import { CashFlowSimulator } from './components/CashFlowSimulator';
import { InvestmentZones } from './components/InvestmentZones';
import { Zones } from './components/Zones';
import { Testimonials } from './components/Testimonials';
import { ContactForm } from './components/ContactForm';
import { FAQ } from './components/FAQ';
import { Footer } from './components/Footer';
import { MarinaChat } from './components/MarinaChat';
import { Toaster } from './components/ui/sonner';
import BlogTulumVsPlaya from './components/BlogTulumVsPlaya';
import BlogInversionRivieraMaya from './components/BlogInversionRivieraMaya';
import BlogAirbnbTulum from './components/BlogAirbnbTulum';
import BlogAirbnbTulumEN from './components/BlogAirbnbTulumEN';
import BlogInvestingTulumForeignBuyers from './components/BlogInvestingTulumForeignBuyers';
import BlogPuertoMorelos from './components/BlogPuertoMorelos';
import BlogLivingPuertoMorelos from './components/BlogLivingPuertoMorelos';
import Privacidad from './components/Privacidad';
import BlogCostOfLiving from './components/BlogCostOfLiving';
import BlogIsPuertoMorelosSafe from './components/BlogIsPuertoMorelosSafe';
import BlogLivingPlayaDelCarmen from './components/BlogLivingPlayaDelCarmen';
import BlogBestNeighborhoodsPlaya from './components/BlogBestNeighborhoodsPlaya';
import BlogTulumRealEstate from './components/BlogTulumRealEstate';
import BlogMovingPuertoMorelos from './components/BlogMovingPuertoMorelos';

const VIDEO_DOLLY_IN = "https://customer-assets.emergentagent.com/job_tulum-investment-lab/artifacts/7z1s8kwf_magnific_slow-cinematic-dollyin-th_gJClll2SXO.mp4";
const VIDEO_ARCHITECTURAL = "https://customer-assets.emergentagent.com/job_tulum-investment-lab/artifacts/5alho77m_magnific_ultra-slow-architectural-_xghAxftjfW.mp4";
const VIDEO_DOLLY_TO = "https://customer-assets.emergentagent.com/job_tulum-investment-lab/artifacts/kd94k0mi_magnific_slow-cinematic-dollyin-to_rltkNukxtc.mp4";

function HomePage() {
  const { language } = useLanguage();

  return (
    <main>
      <Hero />
      <ValueProposition />
      <VideoSection src={VIDEO_DOLLY_IN} height="60vh" overlay={0.55}>
        <div className="text-center px-6">
          <p className="font-playfair text-3xl sm:text-4xl lg:text-5xl text-[#FAFAF7] leading-[1.1] tracking-tight">
            <span className="block">{language === 'es' ? 'Invierte donde el mundo' : 'Invest where the world'}</span>
            <span className="block text-[#2BBFBF] italic">{language === 'es' ? 'quiere estar' : 'wants to be'}</span>
          </p>
        </div>
      </VideoSection>
      <FeaturedProperties />
      <Casas />
      <VideoSection src={VIDEO_ARCHITECTURAL} height="50vh" overlay={0.6}>
        <div className="text-center px-6">
          <p className="font-inter text-[#FAFAF7]/70 text-sm uppercase tracking-[0.3em]">{language === 'es' ? 'Simula tu inversión' : 'Simulate your investment'}</p>
          <p className="font-playfair text-3xl sm:text-4xl lg:text-5xl text-[#FAFAF7] leading-[1.1] tracking-tight mt-4">
            <span className="block">{language === 'es' ? 'Conoce el rendimiento de' : 'Discover the returns of'}</span>
            <span className="block text-[#2BBFBF] italic">{language === 'es' ? 'tu próxima propiedad' : 'your next property'}</span>
          </p>
        </div>
      </VideoSection>
      <CashFlowSimulator />
      <InvestmentZones />
      <Zones />
      <VideoSection src={VIDEO_DOLLY_TO} height="50vh" overlay={0.55}>
        <div className="text-center px-6">
          <p className="font-playfair text-3xl sm:text-4xl lg:text-5xl text-[#FAFAF7] leading-[1.1] tracking-tight">
            <span className="block">{language === 'es' ? 'Lo que dicen' : 'What our'}</span>
            <span className="block text-[#2BBFBF] italic">{language === 'es' ? 'nuestros inversionistas' : 'investors say'}</span>
          </p>
        </div>
      </VideoSection>
      <Testimonials />
      <ContactForm />
      <FAQ />
    </main>
  );
}

function HomePageWithCanonical() {
  useEffect(() => {
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', 'https://mareariviera.com/');
    return () => link.setAttribute('href', 'https://mareariviera.com/');
  }, []);
  return <HomePage />;
}

function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isLoading) {
      const target = sessionStorage.getItem('scrollTo');
      if (target) {
        sessionStorage.removeItem('scrollTo');
        setTimeout(() => {
          const el = document.getElementById(target);
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [isLoading]);

  return (
    <LanguageProvider>
      {isLoading && <LoadingScreen onLoadComplete={() => setIsLoading(false)} />}
      <div className={`App bg-[#FAFAF7] ${isLoading ? 'overflow-hidden h-screen' : ''}`}>
        <Navbar />
        <TopBanner />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/propiedades" element={<HomePageWithCanonical />} />
          <Route path="/cash-flow" element={<HomePageWithCanonical />} />
          <Route path="/contacto" element={<HomePageWithCanonical />} />
          <Route path="/blog/tulum-vs-playa-del-carmen-donde-invertir" element={<BlogTulumVsPlaya />} />
          <Route path="/blog/inversion-inmobiliaria-riviera-maya" element={<BlogInversionRivieraMaya />} />
          <Route path="/blog/airbnb-tulum-cuanto-rinde-departamento" element={<BlogAirbnbTulum />} />
          <Route path="/blog/airbnb-tulum-how-much-does-a-property-earn" element={<BlogAirbnbTulumEN />} />
          <Route path="/blog/investing-tulum-real-estate-foreign-buyers" element={<BlogInvestingTulumForeignBuyers />} />
          <Route path="/blog/invertir-puerto-morelos-riviera-maya" element={<BlogPuertoMorelos />} />
          <Route path="/blog/living-and-investing-in-puerto-morelos-complete-guide-2026" element={<BlogLivingPuertoMorelos />} />
          <Route path="/privacidad" element={<Privacidad />} />
          <Route path="/blog/cost-of-living-in-puerto-morelos-2026" element={<BlogCostOfLiving />} />
          <Route path="/blog/is-puerto-morelos-safe" element={<BlogIsPuertoMorelosSafe />} />
          <Route path="/blog/living-in-playa-del-carmen-2026" element={<BlogLivingPlayaDelCarmen />} />
          <Route path="/blog/best-neighborhoods-playa-del-carmen-invest-2026" element={<BlogBestNeighborhoodsPlaya />} />
          <Route path="/blog/tulum-real-estate-market-2026" element={<BlogTulumRealEstate />} />
          <Route path="/blog/moving-to-puerto-morelos-2026" element={<BlogMovingPuertoMorelos />} />
          {/* 301 Redirects for phantom URLs indexed by Google */}
          <Route path="/blog/guia-inversion-riviera-maya-2026" element={<Navigate to="/blog/inversion-inmobiliaria-riviera-maya" replace />} />
          <Route path="/blog/invertir-en-puerto-morelos" element={<Navigate to="/blog/invertir-puerto-morelos-riviera-maya" replace />} />
          <Route path="/blog/tulum-vs-playa-del-carren-donde-invertir" element={<Navigate to="/blog/tulum-vs-playa-del-carmen-donde-invertir" replace />} />
          <Route path="/blog/cuanto-rinde-depa-tulum-airbnb" element={<Navigate to="/blog/airbnb-tulum-cuanto-rinde-departamento" replace />} />
          <Route path="/blog/cost-of-living-id2026" element={<Navigate to="/blog/cost-of-living-in-puerto-morelos-2026" replace />} />
        </Routes>
        <Footer />
        <MarinaChat />
        <Toaster />
      </div>
    </LanguageProvider>
  );
}

export default App;
